import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { Card, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const ICON_TONES = {
  primary: "text-primary",
  success: "text-success",
  muted: "text-muted-foreground",
  destructive: "text-destructive",
} as const;

export type AssistantCardTone = keyof typeof ICON_TONES;

/**
 * The flat card every assistant surface composes: a border, no shadow, no nested padding of its own,
 * the assistant's radius. On a phone it is flat edge to edge (rule 9): no radius, no side borders.
 */
const AssistantCard = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <Card
      ref={ref}
      className={cn(
        "overflow-hidden rounded-assistant border-border shadow-none max-sm:rounded-none max-sm:border-x-0",
        className
      )}
      {...props}
    />
  )
);
AssistantCard.displayName = "AssistantCard";

interface AssistantCardHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Only where a decision or mockup shows one: card titles stand alone (ui rule 5). */
  icon?: LucideIcon;
  title: React.ReactNode;
  tone?: AssistantCardTone;
}

/** Icon and title on one line, no subtitle; `children` sit at the right edge (a status pill, a badge). */
const AssistantCardHeader = React.forwardRef<HTMLDivElement, AssistantCardHeaderProps>(
  ({ icon: Icon, title, tone = "primary", className, children, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center gap-2 px-3.5 pb-2 pt-3", className)} {...props}>
      {Icon && <Icon className={cn("h-4 w-4 shrink-0", ICON_TONES[tone])} aria-hidden="true" />}
      <CardTitle role="heading" aria-level={3} className="min-w-0 truncate text-sm">
        {title}
      </CardTitle>
      {children && <div className="ml-auto flex shrink-0 items-center gap-2">{children}</div>}
    </div>
  )
);
AssistantCardHeader.displayName = "AssistantCardHeader";

const AssistantCardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("px-3.5 pb-3 pt-1 text-sm", className)} {...props} />
  )
);
AssistantCardBody.displayName = "AssistantCardBody";

const AssistantCardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center gap-2 px-3.5 pb-3", className)} {...props} />
  )
);
AssistantCardFooter.displayName = "AssistantCardFooter";

export { AssistantCard, AssistantCardHeader, AssistantCardBody, AssistantCardFooter };
